import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useSummaryCapture } from "@/hooks/voice/useSummaryCapture";
import SummaryCaptureForm from "@/components/voice-genie/SummaryCapture/SummaryCaptureForm";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

interface SummaryCaptureButtonProps {
  brandColor: string;
  secondaryColor?: string | null;
  agentId?: string;
  agentUserId?: string;
  /** Diameter of the round trigger; matches the other action-row buttons. */
  triggerPx?: number;
  /** Fired with the captured email so the page can let the Genie know. */
  onCaptured?: (email: string) => void;
}

/**
 * Quiet, always-present "Get a summary" affordance for the live-agent action
 * row. One tap opens a compact branded dialog with progressive fields. Never
 * pops up on its own — it just sits there, ready, before / during / after the
 * conversation.
 */
const SummaryCaptureButton = ({
  brandColor,
  secondaryColor,
  agentId,
  agentUserId,
  triggerPx = 36,
  onCaptured,
}: SummaryCaptureButtonProps) => {
  const [open, setOpen] = useState(false);
  const capture = useSummaryCapture({
    agentId,
    agentUserId,
    source: "summary_chip",
    onCaptured,
  });

  // Celebrate, then quietly bow out.
  useEffect(() => {
    if (!capture.submitted || !open) return;
    const t = setTimeout(() => setOpen(false), 1900);
    return () => clearTimeout(t);
  }, [capture.submitted, open]);

  useEffect(() => {
    if (open && agentId) capture.markShown();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, agentId]);

  const iconPx = triggerPx <= 36 ? 18 : 22;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Get a summary by email"
        title="Get a summary by email"
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: triggerPx,
          height: triggerPx,
          borderRadius: 9999,
          background: "white",
          border: `1px solid ${brandColor}33`,
          color: brandColor,
          boxShadow: "0 4px 10px rgba(0,0,0,0.08)",
        }}
      >
        <GenieIcon name="mail" size={iconPx} color={brandColor} />
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm">
          <DialogTitle className="sr-only">Get a summary by email</DialogTitle>
          <SummaryCaptureForm
            brandColor={brandColor}
            secondaryColor={secondaryColor}
            values={capture.values}
            onChange={capture.onChange}
            expanded={capture.expanded}
            onToggleExpanded={capture.toggleExpanded}
            onSubmit={capture.submit}
            isSubmitting={capture.isSubmitting}
            submitted={capture.submitted}
            error={capture.error}
            alreadyCapturedEmail={capture.alreadyCaptured && !capture.editing ? capture.capturedEmail : null}
            onChangeEmail={capture.startEdit}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SummaryCaptureButton;
